import React, { forwardRef, useState } from 'react';
import Type from 'prop-types';
import { noop, groupBy } from 'lodash';
import { AppBar as MuiAppBar, Toolbar, IconButton } from "@mui/material";
import { ArrowBack as ArrowBackIcon, Close as CloseIcon } from "@mui/icons-material";
import { useTranslation } from 'hooks';
import ConditionalWrap from '../ConditionalWrap';
import HideOnScroll from '../HideOnScroll';
import AppBarAction from './AppBarAction';
import AppBarContent from './AppBarContent';
import OverflowMenu from './OverflowMenu';

const icons = {
  back: ArrowBackIcon,
  close: CloseIcon,
};

const AppBar = forwardRef(function AppBar(props, ref) {
  const { t } = useTranslation();
  const [overflowMenuAnchorEl, setAnchorEl] = useState(null);

  const {
    title,
    subtitle,
    navButtonAriaLabel = t('back'),
    navIcon,
    onNavClick,
    children,
    hideOnScroll,
    additionalSlot,
    actions,
    actionsIconSx,
    onActionSelected,
    loading,
    ...other
  } = props;

  const { always: appBarActions = [], never: overflowMenuActions = [] } = groupBy(
    actions,
    (action) => action.show,
  );

  const handleOverflowMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleOverflowMenuClose = () => {
    setAnchorEl(null);
  };

  const renderAppBarActions = () => {
    return appBarActions.map((action) => (
      <AppBarAction
        key={action.id}
        action={action}
        onActionSelected={onActionSelected}
        iconSx={actionsIconSx}
      />
    ));
  };

  const renderNavIcon = () => {
    const NavIcon = icons[navIcon];

    if (!NavIcon) {
      return null;
    }

    return (
      <IconButton edge="start" color="inherit" aria-label={navButtonAriaLabel} onClick={onNavClick}>
        <NavIcon />
      </IconButton>
    );
  };

  return (
    <ConditionalWrap
      condition={hideOnScroll}
      wrap={(childrens) => <HideOnScroll>{childrens}</HideOnScroll>}
    >
      <MuiAppBar ref={ref} {...other}>
        <Toolbar>
          {renderNavIcon()}

          <AppBarContent title={title} subtitle={subtitle} loading={loading} />

          {renderAppBarActions()}

          {overflowMenuActions.length > 0 && (
            <OverflowMenu
              actions={overflowMenuActions}
              anchorEl={overflowMenuAnchorEl}
              onOpen={handleOverflowMenuOpen}
              onClose={handleOverflowMenuClose}
              onActionSelected={onActionSelected}
            />
          )}

          {children}
        </Toolbar>

        {additionalSlot}
      </MuiAppBar>
    </ConditionalWrap>
  );
});

AppBar.propTypes = {
  title: Type.oneOfType([Type.string, Type.node]),
  subtitle: Type.oneOfType([Type.string, Type.node]),
  position: Type.oneOf(['absolute', 'fixed', 'relative', 'static', 'sticky']),
  navButtonAriaLabel: Type.string, // eslint-disable-line react/require-default-props
  navIcon: Type.oneOf(['back', 'close']),
  loading: Type.bool,
  actions: Type.arrayOf(Type.shape(AppBarAction.propTypes.action)),
  children: Type.node,
  elevation: Type.number,
  hideOnScroll: Type.bool,
  additionalSlot: Type.node,
  actionsIconSx: Type.oneOfType([Type.object, Type.array, Type.func]),
  onNavClick: Type.func,
  onActionSelected: Type.func,
};

AppBar.defaultProps = {
  position: 'sticky',
  title: null,
  subtitle: null,
  actions: [],
  elevation: 1,
  hideOnScroll: false,
  loading: false,
  children: null,
  additionalSlot: null,
  actionsIconSx: undefined,
  navIcon: null,
  onNavClick: noop,
  onActionSelected: noop,
};

export default AppBar;
